/**
 * /dashboard/admin/podcast — Admin podcast manager.
 *
 * Three things in one place:
 *   1. Schedule shows (live or upload, with thumbnail)
 *   2. Start a scheduled live show (launches JaaS room — same wizard config as therapy)
 *   3. Publish an upload show (audio/video file or URL → published episode)
 *   4. Quick-add an episode (no scheduling)
 */
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mic, Plus, Calendar, Video, Upload, ImageIcon, Trash2, Radio, FileAudio,
  CheckCircle2, X, ExternalLink, Loader2, Heart, UserPlus,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { HostPodcastTools, SceneOverlay, ShareMenu, ViewerTools, useScene } from "../../components/live/PodcastLayer";
import { LiveStage } from "../../components/live/LiveStage";
import { ClipsTab } from "../../components/podcast/ClipsTab";
import { uploadChunked } from "../../lib/stageRecorder";
import { useAuth } from "../../lib/auth";
import { TestimonialsTab } from "../../components/podcast/TestimonialsTab";

function AdminGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

const TABS = [
  { v: "scheduled", l: "Scheduled" },
  { v: "episodes", l: "Episodes" },
  { v: "clips", l: "Intros & clips" },
  { v: "testimonials", l: "Client testimonials" },
];

export default function AdminPodcastPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "scheduled");
  return (
    <AdminGate>
      <div data-testid="admin-podcast-page" className="space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin · Podcast</p>
          <h1 className="font-serif font-light text-4xl">Podcast Studio</h1>
          <p className="text-[var(--muted)] text-sm mt-2">
            Schedule live shows, upload episodes, manage thumbnails — all in one place.
          </p>
        </div>
        <div className="flex gap-2 border-b border-[var(--line)]">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-[2px] ${
                tab === t.v ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-black/50 hover:text-black"
              }`}
              data-testid={`admin-podcast-tab-${t.v}`}
            >{t.l}</button>
          ))}
        </div>
        {tab === "scheduled" && <ScheduledTab />}
        {tab === "episodes" && <EpisodesTab />}
        {tab === "clips" && <ClipsTab />}
        {tab === "testimonials" && <TestimonialsTab />}
      </div>
    </AdminGate>
  );
}

/* ============== Scheduled tab ============== */
function ScheduledTab() {
  const [rows, setRows] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [publishingShow, setPublishingShow] = useState(null);

  async function load() {
    try { setRows((await api.get("/admin/podcast/scheduled")).data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function del(show_id) {
    if (!window.confirm("Delete this show?")) return;
    try { await api.delete(`/admin/podcast/scheduled/${show_id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowCreate(true)} className="btn-gold" data-testid="podcast-schedule-btn">
          <Plus className="w-4 h-4" /> Schedule a show
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-12 text-sm text-black/40">No scheduled shows yet.</div>
      ) : (
        <div className="space-y-2" data-testid="admin-podcast-scheduled-list">
          {rows.map((s) => (
            <div key={s.show_id} className="bg-white border border-[var(--line)] rounded-xl p-4 flex items-center gap-4 flex-wrap"
              data-testid={`podcast-scheduled-${s.show_id}`}>
              {s.thumbnail_url && (
                <img src={s.thumbnail_url.startsWith("http") ? s.thumbnail_url : `${BACKEND_URL}${s.thumbnail_url}`}
                  className="w-16 h-16 rounded-lg object-cover" alt="" />
              )}
              <div className="flex-1 min-w-[180px]">
                <p className="font-serif text-lg flex items-center gap-2">
                  {s.title}
                  {s.mode === "live" ? <Radio className="w-4 h-4 text-rose-500" /> : <FileAudio className="w-4 h-4 text-[var(--gold)]" />}
                </p>
                <p className="text-xs text-black/50">{(s.scheduled_at || "").slice(0, 16).replace("T", " ")} · {s.duration_min} min</p>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                s.status === "live" ? "bg-rose-100 text-rose-700 animate-pulse"
                : s.status === "published" ? "bg-emerald-100 text-emerald-700"
                : s.status === "ended" ? "bg-black/10"
                : s.status === "recorded" ? "bg-sky-100 text-sky-700"
                : "bg-amber-100 text-amber-700"
              }`}>{s.status}</span>
              <div className="flex gap-2">
                {s.mode === "live" && !["published", "ended", "recorded"].includes(s.status) && (
                  <a href={`/podcast/live/${s.show_id}`} target="_blank" rel="noopener"
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#C8A96B]/40 hover:bg-[#C8A96B]/10"
                    data-testid={`podcast-go-live-${s.show_id}`}>
                    <Radio className="w-3 h-3 inline mr-1" /> {s.status === "live" ? "Re-enter" : "Go live"}
                  </a>
                )}
                {(s.mode === "upload" || s.recording_url) && s.status !== "published" && (
                  <button onClick={() => setPublishingShow(s)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                    data-testid={`podcast-publish-${s.show_id}`}>
                    <Upload className="w-3 h-3 inline mr-1" /> {s.recording_url ? "Publish recording" : "Publish"}
                  </button>
                )}
                {s.mode === "live" && !["published", "ended", "recorded"].includes(s.status) && (
                  <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/podcast/live/${s.show_id}`).then(() => toast.success("Viewer link copied"))}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10" data-testid={`podcast-viewer-link-${s.show_id}`}>Copy viewer link</button>
                )}
                <button onClick={() => del(s.show_id)} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`podcast-del-${s.show_id}`}>
                  <Trash2 className="w-3 h-3 inline" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <ScheduleModal onClose={() => { setShowCreate(false); load(); }} />}
      {publishingShow && <PublishModal show={publishingShow} onClose={() => { setPublishingShow(null); load(); }} />}
    </div>
  );
}

function ScheduleModal({ onClose }) {
  const [f, setF] = useState({
    title: "", description: "", topic: "real-couples", mode: "live",
    duration_min: 30, scheduled_at: "", thumbnail_url: "", default_layout: "pip",
  });
  const [thumbFile, setThumbFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function set(k, v) { setF((p) => ({ ...p, [k]: v })); }

  async function submit() {
    if (!f.title.trim() || !f.scheduled_at) return toast.error("Title + date/time required.");
    setBusy(true);
    try {
      let body = { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() };
      if (thumbFile) {
        body.thumbnail_data_url = await fileToDataUrl(thumbFile);
      }
      await api.post("/admin/podcast/scheduled", body);
      toast.success("Show scheduled.");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="podcast-schedule-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-3xl">Schedule a show</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-black/40" /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => set("mode", "live")} className={`px-4 py-3 rounded-xl border-2 ${f.mode === "live" ? "border-[var(--gold)] bg-[#C8A96B]/5" : "border-[var(--line)]"}`}
              data-testid="podcast-mode-live">
              <Radio className="w-5 h-5 mx-auto text-rose-500 mb-1" />
              <p className="text-sm font-medium">Live show</p>
              <p className="text-[10px] text-black/50">Go live on camera right here, recorded automatically</p>
            </button>
            <button onClick={() => set("mode", "upload")} className={`px-4 py-3 rounded-xl border-2 ${f.mode === "upload" ? "border-[var(--gold)] bg-[#C8A96B]/5" : "border-[var(--line)]"}`}
              data-testid="podcast-mode-upload">
              <FileAudio className="w-5 h-5 mx-auto text-[var(--gold)] mb-1" />
              <p className="text-sm font-medium">Upload</p>
              <p className="text-[10px] text-black/50">Pre-recorded audio/video</p>
            </button>
          </div>
          {f.mode === "live" && <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Starting layout for viewers</label>
            <select value={f.default_layout} onChange={(e) => set("default_layout", e.target.value)} className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]" data-testid="podcast-default-layout">
              <option value="pip">Picture in picture (guests in the corner)</option><option value="side">Side by side</option><option value="spotlight">Host spotlight</option><option value="grid">Equal grid</option>
            </select></div>}

          <Inp label="Title *" v={f.title} on={(v) => set("title", v)} testid="podcast-title" />
          <Area label="Description" v={f.description} on={(v) => set("description", v)} testid="podcast-description" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-black/50">Scheduled at</label>
              <input type="datetime-local" value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)}
                className="w-full mt-1 px-3 py-3 rounded-xl border border-[var(--line)]" data-testid="podcast-scheduled-at" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-black/50">Duration (min)</label>
              <input type="number" value={f.duration_min} onChange={(e) => set("duration_min", Number(e.target.value))}
                className="w-full mt-1 px-3 py-3 rounded-xl border border-[var(--line)]" data-testid="podcast-duration" />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Thumbnail</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <input type="file" accept="image/*" onChange={(e) => setThumbFile(e.target.files?.[0])}
                className="text-xs border border-[var(--line)] rounded-lg px-3 py-2" data-testid="podcast-thumb-file" />
              <input value={f.thumbnail_url} onChange={(e) => set("thumbnail_url", e.target.value)}
                placeholder="...or paste URL"
                className="text-xs border border-[var(--line)] rounded-lg px-3 py-2" data-testid="podcast-thumb-url" />
            </div>
          </div>
        </div>

        <button onClick={submit} disabled={busy} className="btn-gold w-full mt-5" data-testid="podcast-schedule-submit">
          <Calendar className="w-4 h-4" /> {busy ? "Saving…" : "Schedule"}
        </button>
      </div>
    </div>
  );
}

function PublishModal({ show, onClose }) {
  const [f, setF] = useState({ audio_url: "", video_url: show.recording_url || "", duration_min: show.duration_min });
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit() {
    if (!f.audio_url && !f.video_url && !audioFile && !videoFile) {
      return toast.error("Provide audio or video (file or URL).");
    }
    setBusy(true);
    try {
      const body = { ...f };
      if (audioFile) body.audio_data_url = await fileToDataUrl(audioFile);
      if (videoFile) body.video_data_url = await fileToDataUrl(videoFile);
      await api.post(`/admin/podcast/scheduled/${show.show_id}/publish`, body);
      toast.success("Episode published.");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="podcast-publish-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl">Publish "{show.title}"</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-black/40" /></button>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-black/50">Provide a URL OR upload a file for either audio or video. Both is fine.</p>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Audio (mp3 / wav / m4a)</label>
            <input type="file" accept="audio/*" onChange={(e) => setAudioFile(e.target.files?.[0])}
              className="block w-full text-xs mt-1" data-testid="publish-audio-file" />
            <input value={f.audio_url} onChange={(e) => setF((p) => ({ ...p, audio_url: e.target.value }))}
              placeholder="...or paste an audio URL"
              className="w-full px-3 py-2 rounded-lg border border-[var(--line)] text-xs mt-2" data-testid="publish-audio-url" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Video (mp4 / webm)</label>
            <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0])}
              className="block w-full text-xs mt-1" data-testid="publish-video-file" />
            <input value={f.video_url} onChange={(e) => setF((p) => ({ ...p, video_url: e.target.value }))}
              placeholder="...or paste a video URL"
              className="w-full px-3 py-2 rounded-lg border border-[var(--line)] text-xs mt-2" data-testid="publish-video-url" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-black/50">Duration (min)</label>
            <input type="number" value={f.duration_min}
              onChange={(e) => setF((p) => ({ ...p, duration_min: Number(e.target.value) }))}
              className="w-full px-3 py-2 rounded-lg border border-[var(--line)]" data-testid="publish-duration" />
          </div>
        </div>

        <button onClick={submit} disabled={busy} className="btn-gold w-full mt-5" data-testid="publish-submit">
          <Upload className="w-4 h-4" /> {busy ? "Publishing…" : "Publish episode"}
        </button>
      </div>
    </div>
  );
}

/* ============== Episodes tab ============== */
function EpisodesTab() {
  const [rows, setRows] = useState([]);

  async function load() {
    try { setRows((await axios.get(`${BACKEND_URL}/api/podcast/episodes`)).data || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function del(eid) {
    if (!window.confirm("Delete this episode for good?")) return;
    try { await api.delete(`/podcast/episodes/${eid}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-2" data-testid="admin-podcast-episodes-list">
      {rows.length === 0 ? <p className="text-sm text-black/40 text-center py-12">No episodes yet.</p>
       : rows.map((e) => (
        <div key={e.episode_id} className="bg-white border border-[var(--line)] rounded-xl p-4 flex items-center gap-4 flex-wrap"
          data-testid={`podcast-episode-${e.episode_id}`}>
          {e.thumbnail_url && (
            <img src={e.thumbnail_url.startsWith("http") ? e.thumbnail_url : `${BACKEND_URL}${e.thumbnail_url}`}
              alt="" className="w-16 h-16 rounded-lg object-cover" />
          )}
          <div className="flex-1 min-w-[180px]">
            <p className="font-serif text-lg">#{e.episode_number} · {e.title}</p>
            <p className="text-xs text-black/50">{e.topic} · {e.duration_min}m</p>
          </div>
          <button onClick={() => del(e.episode_id)} className="text-xs p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
            data-testid={`podcast-episode-del-${e.episode_id}`} aria-label="Delete episode">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ============== Helpers ============== */
function Inp({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}
function Area({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-black/50">{label}</label>
      <textarea value={v} onChange={(e) => on(e.target.value)} rows={3}
        className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
        data-testid={testid} />
    </div>
  );
}
function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/* ============== /podcast/live/:id — public live show room ============== */
function CopyGuestLink({ id }) {
  async function copy() {
    try { const { data } = await api.post(`/admin/podcast/scheduled/${id}/stage-link`); await navigator.clipboard.writeText(`${window.location.origin}/podcast/live/${id}?stage=${data.stage_key}`); toast.success("Guest link copied: they join on camera"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return <button onClick={copy} className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center" aria-label="Copy guest link" title="Copy co-host link" data-testid="podcast-copy-guest-link"><UserPlus className="w-5 h-5" /></button>;
}

function PodcastRoom({ join, show, id, user, onLeave, saveRecording }) {
  const [room, setRoom] = useState(null);
  const scene = useScene(room);
  const [mine, setMine] = useState(null);
  const [clips, setClips] = useState([]);
  const [gate, setGate] = useState(false);
  const outroDone = useRef(null);
  const [ending, setEnding] = useState(false);
  const introPlayed = useRef(false);
  const host = join.host;
  const layout = mine || scene.layout || show.default_layout || "pip";
  const url = `${window.location.origin}/podcast/live/${id}`;
  useEffect(() => { if (host) api.get("/admin/podcast/clips").then((r) => setClips(r.data)).catch(() => {}); }, [host]);
  useEffect(() => { if (user) return undefined; const t = setTimeout(() => setGate(true), 5000); return () => clearTimeout(t); }, [user]);
  useEffect(() => {
    if (!host || !room || introPlayed.current || !clips.length) return undefined;
    const intro = clips.find((c) => c.role === "intro" && c.is_default);
    if (!intro) return undefined;
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (introPlayed.current || tries > 120) return clearInterval(t);
      if (room.status !== "live") return undefined;
      introPlayed.current = true;
      clearInterval(t);
      if (!room.sceneNow?.intro_done) room.scene({ ...(room.sceneNow || {}), intro_done: true, media: { url: intro.url, kind: intro.kind, mode: "full", role: "intro", started: Date.now() / 1000 } });
      return undefined;
    }, 1000);
    return () => clearInterval(t);
  }, [host, room, clips]);
  const onMediaEnded = () => {
    if (!room) return;
    room.scene({ ...(room.sceneNow || {}), media: null });
    if (outroDone.current) { outroDone.current(); outroDone.current = null; }
  };
  const endNow = () => { if (outroDone.current) { outroDone.current(); outroDone.current = null; } };
  const beforeEnd = () => {
    const outro = clips.find((c) => c.role === "outro" && c.is_default);
    if (!outro || !room) return Promise.resolve();
    toast("Playing your outro, then ending the live");
    room.scene({ ...(room.sceneNow || {}), overlay: "", media: { url: outro.url, kind: outro.kind, mode: "full", role: "outro", started: Date.now() / 1000 } });
    setEnding(true);
    return new Promise((res) => { outroDone.current = res; setTimeout(() => { if (outroDone.current) { outroDone.current(); outroDone.current = null; } }, outro.kind === "youtube" ? 60000 : 300000); });
  };
  const endShow = () => api.post(`/admin/podcast/scheduled/${id}/end-live`).catch(() => {});
  const pickHostLayout = (l) => { room?.scene({ ...(room.sceneNow || {}), layout: l }); api.put(`/admin/podcast/scheduled/${id}/layout`, { default_layout: l }).catch(() => {}); };
  return (
    <>
      <LiveStage join={join} title={show.title || "Podcast live"} onLeave={onLeave} onEnd={host ? endShow : undefined} onRecorded={host ? saveRecording : undefined} autoRecord={host}
        hostTools={<><CopyGuestLink id={id} /><HostPodcastTools room={room} scene={scene} clips={clips} layout={layout} onLayout={pickHostLayout} ending={ending} onEndNow={endNow} /></>}
        viewerTools={<ViewerTools layout={layout} setLayout={setMine} url={url} title={show.title} hideLayout={host} />} tileMark={false}
        layout={layout} overlay={<SceneOverlay scene={scene} isHost={host} onMediaEnded={onMediaEnded} skew={room?.skew || 0} />} onRoom={setRoom} endLabel="End live" beforeEnd={host ? beforeEnd : null} hideBubbles />
      {gate && <div className="absolute inset-0 z-[60] backdrop-blur-xl bg-black/60 flex items-center justify-center p-6" data-testid="podcast-signin-gate">
        <div className="bg-white text-[var(--ink)] rounded-3xl p-8 max-w-sm w-full text-center">
          <img src="/brand/logo-mark.png" alt="" className="w-14 mx-auto mb-3" />
          <p className="font-serif text-3xl">Keep watching</p>
          <p className="text-sm text-black/60 mt-2">Sign in free to keep watching {show.title} live, chat and react.</p>
          <a href={`/login?next=${encodeURIComponent(`/podcast/live/${id}`)}`} className="btn-gold w-full mt-6 inline-flex justify-center" data-testid="podcast-signin-gate-btn">Sign in to continue</a>
        </div>
      </div>}
    </>
  );
}

export function PodcastLiveRoomPage() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [show, setShow] = useState(null);
  const [join, setJoin] = useState(null);
  const [step, setStep] = useState("loading");
  const [error, setError] = useState("");
  const stageKey = new URLSearchParams(window.location.search).get("stage") || "";
  const isHost = user?.role === "admin";

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/podcast/live/${id}`).then((r) => {
      setShow(r.data);
      if (["ended", "recorded", "published"].includes(r.data.status)) { setStep("error"); setError("This live show has ended. Catch the episode on the podcast page."); }
      else setStep("ready");
    }).catch(() => { setStep("error"); setError("Show not found."); });
  }, [id]);

  async function joinNow() {
    const onCamera = isHost || stageKey;
    if (onCamera && !user) { nav(`/login?next=${encodeURIComponent(`/podcast/live/${id}${stageKey ? `?stage=${stageKey}` : ""}`)}`); return; }
    setStep("starting");
    try {
      const r = onCamera ? await axios.post(`${BACKEND_URL}/api/podcast/scheduled/${id}/join`, { stage_key: stageKey }, { withCredentials: true })
        : await axios.post(`${BACKEND_URL}/api/podcast/scheduled/${id}/watch`, {}, { withCredentials: true });
      setJoin(r.data); setStep("live");
    } catch (e) { setError(e?.response?.data?.detail || "Failed to join."); setStep("error"); }
  }

  async function saveRecording(blob) {
    await toast.promise(uploadChunked(`/admin/podcast/scheduled/${id}/recording`, blob, api), {
      loading: "Saving your recording…", success: "Recording saved. Publish it from Admin → Podcast.", error: (e) => formatApiError(e),
    });
  }
  if (step === "loading" || loading) return <div className="min-h-screen flex items-center justify-center text-sm text-black/40">Loading…</div>;
  if (step === "error") return <div className="min-h-screen flex flex-col gap-4 items-center justify-center text-sm text-red-600" data-testid="podcast-live-error">{error}<a href="/podcast" className="btn-gold text-xs">Go to the podcast</a></div>;

  if (step === "ready") {
    const label = isHost ? "Go live as host" : stageKey ? (user ? "Join on camera" : "Sign in to join on camera") : "Watch live";
    return (
      <div className="min-h-screen bg-[var(--ink)] text-white flex items-center justify-center p-6" data-testid="podcast-live-gate">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-md w-full text-center backdrop-blur">
          <img src="/brand/logo-mark.png" alt="" className="w-12 mx-auto mb-3" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--gold)] flex items-center justify-center gap-1"><Radio className="w-3.5 h-3.5" />{show?.status === "live" ? "Live now" : "Live podcast"}</p>
          <h1 className="font-serif text-3xl mt-2">{show?.title}</h1>
          {show?.description && <p className="text-sm text-white/60 mt-2">{show.description}</p>}
          {show?.thumbnail_url && <img src={show.thumbnail_url.startsWith("http") ? show.thumbnail_url : `${BACKEND_URL}${show.thumbnail_url}`} alt="" className="rounded-xl mt-4 mx-auto max-h-48 object-cover" />}
          <button onClick={joinNow} className="btn-gold mt-6 w-full inline-flex justify-center gap-2" data-testid="podcast-live-join"><Video className="w-4 h-4" /> {label}</button>
          <div className="mt-4 flex justify-center"><ShareMenu url={`${window.location.origin}/podcast/live/${id}`} title={show?.title || "Live podcast"} /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black z-50" data-testid="podcast-live-room">
      {join ? <PodcastRoom join={join} show={show} id={id} user={user} onLeave={() => nav("/podcast")} saveRecording={saveRecording} />
        : <div className="absolute inset-0 flex items-center justify-center text-white"><Loader2 className="w-10 h-10 animate-spin" /></div>}
    </div>
  );
}
